/**
 * Quantidades da movimentação em duas etapas.
 *
 * O middleware manda `Decimal(18,6)` como texto na escala do banco
 * (`10.000000`). O painel nunca passa isso por `Number`: `0.1 + 0.2` no
 * JavaScript não é `0.3`, e uma conta errada no diálogo da divergência faria o
 * supervisor decidir em cima de um número que o ERP nunca vai receber. As
 * contas correm em inteiros de milionésimos (`bigint`), a mesma escala do banco.
 */

const CASAS = 6;
const ESCALA = 10n ** BigInt(CASAS);
const DECIMAL = /^(-?)(\d+)(?:\.(\d{1,6}))?$/;

/** Texto decimal em milionésimos; `null` quando não é um decimal que se leia. */
function emMilionesimos(texto: string): bigint | null {
  const partes = DECIMAL.exec(texto.trim());

  if (partes === null) {
    return null;
  }

  const [, sinal = '', inteiro = '0', fracao = ''] = partes;
  const valor = BigInt(inteiro) * ESCALA + BigInt(fracao.padEnd(CASAS, '0'));
  return sinal === '-' ? -valor : valor;
}

/** Milionésimos de volta ao texto na escala do banco (`4.000000`). */
function naEscalaDoBanco(valor: bigint): string {
  const sinal = valor < 0n ? '-' : '';
  const absoluto = valor < 0n ? -valor : valor;
  const inteiro = absoluto / ESCALA;
  const fracao = String(absoluto % ESCALA).padStart(CASAS, '0');
  return `${sinal}${String(inteiro)}.${fracao}`;
}

/**
 * Quantidade como o armazém lê: vírgula decimal e sem os zeros de escala.
 *
 * Só tira zero à direita — nunca arredonda. `5.500000` vira `5,5`, e
 * `0.000001` continua `0,000001`. O que não for decimal volta como veio.
 */
export function formatarQuantidade(texto: string): string {
  const partes = DECIMAL.exec(texto.trim());

  if (partes === null) {
    return texto;
  }

  const [, sinal = '', inteiro = '0', fracao = ''] = partes;
  const significativa = fracao.replace(/0+$/, '');
  return significativa === '' ? `${sinal}${inteiro}` : `${sinal}${inteiro},${significativa}`;
}

/** O que a linha da divergência traz para as contas do diálogo. */
export type SaldoDaDivergencia = {
  /** Quantidade iniciada. */
  readonly quantidade: string;
  /** Soma de TODAS as entregas, a retida inclusive. */
  readonly entregue: string;
  /** O que o operador informou na entrega retida. */
  readonly retida: string;
};

type Operandos = {
  readonly quantidade: bigint;
  readonly entregue: bigint;
  readonly retida: bigint;
};

function operandos(saldo: SaldoDaDivergencia): Operandos | null {
  const quantidade = emMilionesimos(saldo.quantidade);
  const entregue = emMilionesimos(saldo.entregue);
  const retida = emMilionesimos(saldo.retida);

  return quantidade === null || entregue === null || retida === null
    ? null
    : { quantidade, entregue, retida };
}

/** Soma das entregas feitas antes da retida — as que já seguiram para o ERP. */
export function entregueAntesDaRetida(saldo: SaldoDaDivergencia): string | null {
  const valores = operandos(saldo);
  return valores === null ? null : naEscalaDoBanco(valores.entregue - valores.retida);
}

/** `Manter`: a entrega retida leva ao ERP só o que faltava da iniciada. */
export function quantidadeQueFaltava(saldo: SaldoDaDivergencia): string | null {
  const valores = operandos(saldo);
  return valores === null
    ? null
    : naEscalaDoBanco(valores.quantidade - (valores.entregue - valores.retida));
}

/** `Atualizar`: a movimentação passa a valer a soma das entregas. */
export function somaDasEntregas(saldo: SaldoDaDivergencia): string | null {
  const valores = operandos(saldo);
  return valores === null ? null : naEscalaDoBanco(valores.entregue);
}
